import React, { Component } from 'react';

import {
  ClickableComponent,
  FontAwesomeIcon,
  Text,
} from '@schoolinks/ui-components';
import cn from 'classnames';
import { isEqual, get, isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';

import { withState } from 'recompose';

import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faLockKeyhole } from '@fortawesome/pro-regular-svg-icons/faLockKeyhole';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import Collapse from '@mui/material/Collapse';
import Drawer from '@mui/material/Drawer';

import {
  MENU_ICON_MAP_ENH,
  MENU_ICON_LINK_MAP,
} from 'components/Common/LayoutV2/constants';
import ResponsivePrompt from 'components/Common/LayoutV2/SideNav/MobileSideNav/ResponsivePrompt';
import {
  getRelevantNavItems,
  getHiddenNavItem,
  isElementaryPage,
  getFormattedRoute,
  getIsMenuActive,
} from 'components/Common/LayoutV2/utils';
import withRouter from 'containers/withRouter';
import withUserTypeAndOrgInfo from 'containers/withUserTypeAndOrgInfo';
import withModal from 'HOC/withModal';
import ModalPropTypes, { ModalDefaultProps } from 'propTypes/ModalPropTypes';
import { RouterPropTypes } from 'propTypes/RouterPropTypes';

import s from './MobileSideNav.css';

@withRouter
@withUserTypeAndOrgInfo
@withModal
@connect(
  ({ user: { layout = {}, type = [], currentUserMode } = {} }) => ({
    sideNav: getRelevantNavItems(layout, type, 'sideNav', currentUserMode),
  }),
  null,
)
@withState('openMenuIndex', 'setOpenMenuIndex', null)
@withState('possiblePush', 'setPossiblePush', null)
export default class MobileSideNav extends Component {
  shouldComponentUpdate(nextProps, nextStates) {
    return !isEqual(nextProps, this.props) || !isEqual(nextStates, this.state);
  }

  onMobileCancel = () => {
    const { setPossiblePush } = this.props;
    setPossiblePush(null);
  };

  toggleMenu = key => {
    const { openMenuIndex, setOpenMenuIndex } = this.props;
    const updatedIndex = key === openMenuIndex ? null : key;
    setOpenMenuIndex(updatedIndex);
  };

  render() {
    const {
      router: { location: { pathname } = {}, navigate, match },
      user,
      sideNav,
      appbarOpen,
      toggleAppbar = () => null,
      openMenuIndex,
      possiblePush,
    } = this.props;

    const baseNav = sideNav?.find(el => el.type === 'base');
    const isHomeActive = ['/dashboard', '/newsfeed'].some(route =>
      pathname.startsWith(route),
    );

    return (
      <>
        {possiblePush && <ResponsivePrompt onCancel={this.onMobileCancel} />}
        <Drawer
          anchor="left"
          open={!possiblePush && appbarOpen}
          onClose={toggleAppbar}
          classes={{
            paper: s.drawerPaper,
          }}
        >
          <div className={s.header}>
            <ClickableComponent
              aria-label="Toggle app bar"
              className={s.closeIcon}
              onClick={toggleAppbar}
            >
              <FontAwesomeIcon icon={faXmark} />
            </ClickableComponent>
          </div>

          <div className={s.menuContainer}>
            <ClickableComponent
              key="home"
              onClick={() => navigate('/')}
              className={cn(s.menuItem, {
                [s.activeMenuItem]: isHomeActive,
              })}
            >
              <div className={s.menuItemChildren}>
                <div className={s.flexCenter}>
                  <div className={s.menuTitle}>Home</div>
                </div>
              </div>
            </ClickableComponent>
            {baseNav &&
              baseNav.items.map(menu => {
                const { key, name, menuOptions } = menu;
                const isElementary = isElementaryPage(pathname);
                const MENU_ICON_MAP = MENU_ICON_MAP_ENH({ isElementary, user });
                const menuContext = get(MENU_ICON_MAP, menu.key);
                const isMenuItemHidden = getHiddenNavItem({ menu });
                const orgUuid = baseNav?.orgUuid;

                if (isMenuItemHidden) {
                  return null;
                }
                const isMenuActive = getIsMenuActive({
                  menuContext,
                  pathname,
                  orgUuid,
                });
                const isMenuActiveSpecialCondition = get(
                  menuContext,
                  'activeCondition',
                  () => null,
                )(match, pathname, isMenuActive);
                const MenuIcon = get(MENU_ICON_MAP, key);
                const link = get(MENU_ICON_LINK_MAP, key);

                const isActive =
                  isMenuActiveSpecialCondition !== null
                    ? isMenuActiveSpecialCondition
                    : isMenuActive;

                if (!(menuOptions || []).length && link)
                  return (
                    <NavLink
                      key={key}
                      className={cn(s.menuItem, {
                        [s.activeMenuItem]:
                          isMenuActiveSpecialCondition !== null
                            ? isMenuActiveSpecialCondition
                            : isMenuActive,
                      })}
                      to={link}
                    >
                      <div className={s.menuItemChildren}>
                        <div className={s.flexCenter}>
                          <FontAwesomeIcon
                            icon={isActive ? MenuIcon.solid : MenuIcon.regular}
                            size="xl"
                          />
                          <div className={s.menuTitle}>{name}</div>
                        </div>
                      </div>
                    </NavLink>
                  );

                return (
                  <div
                    key={key}
                    className={cn(s.menuItem, {
                      [s.activeMenuItem]:
                        isMenuActiveSpecialCondition !== null
                          ? isMenuActiveSpecialCondition
                          : isMenuActive,
                    })}
                  >
                    <div
                      className={s.menuItemChildren}
                      {...((menuOptions || []).length
                        ? {
                            role: 'button',
                            tabIndex: 0,
                            onClick: () => this.toggleMenu(key),
                          }
                        : {})}
                    >
                      <div className={s.flexCenter}>
                        <FontAwesomeIcon
                          icon={isActive ? MenuIcon.solid : MenuIcon.regular}
                          size="xl"
                        />
                        <div className={s.menuTitle}>{name}</div>
                      </div>

                      {(menuOptions || []).length ? (
                        <div className={s.menuToggleIcon}>
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            rotation={openMenuIndex === key ? 180 : 0}
                          />
                        </div>
                      ) : null}
                    </div>

                    <Collapse in={openMenuIndex === key}>
                      <ul className={s.submenuList}>
                        {(menuOptions || []).map(subMenu => {
                          const {
                            key: subMenuId,
                            name: subMenuTitle,
                            isEnabled,
                          } = { ...subMenu };

                          const subMenuLink = getFormattedRoute({
                            orgUuid,
                            navItem: subMenu,
                            user,
                          });

                          if (isEmpty(subMenuLink)) {
                            console.warn(
                              `${subMenuId} does not have mapping to any link in NAV_LINK_MAP, please add one in order for it to show up on the side nav`,
                            );
                          }

                          if (!isEnabled)
                            return (
                              <li
                                key={subMenuId}
                                className={s.disabledSubMenuItem}
                              >
                                <Text
                                  color="secondary"
                                  className={s.subMenuItem}
                                >
                                  {subMenuTitle}
                                </Text>
                                <div className={s.lockIcon}>
                                  <FontAwesomeIcon
                                    color="secondary"
                                    icon={faLockKeyhole}
                                  />
                                </div>
                              </li>
                            );

                          if (subMenuLink) {
                            return (
                              <li key={subMenuId}>
                                <Text
                                  component={NavLink}
                                  to={subMenuLink}
                                  className={s.subMenuItem}
                                >
                                  {subMenuTitle}
                                </Text>
                              </li>
                            );
                          }

                          return null;
                        })}
                      </ul>
                    </Collapse>
                  </div>
                );
              })}
          </div>
        </Drawer>
      </>
    );
  }
}

MobileSideNav.propTypes = {
  ...ModalPropTypes,
  router: RouterPropTypes,
};

MobileSideNav.defaultProps = {
  ...ModalDefaultProps,
  router: undefined,
};
